import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { INSAutoCompleteGroup, INSAutoCompleteOption } from '@norfolk-southern/accessns-components';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { NSCommonConstants } from '@ruby/configs/common.constants';
import { NSRateConstants } from '@ruby/configs/rate-inquiry.constants';
import { IRateInquiryFavoriteItem, IRateInquiryItem, IRateSearch } from '@ruby/modules/rate-inquiry/models/rate-inquiry.interface';
import { EFavoriteApplicationType } from '@ruby/shared/enums/favorites.enums';
import { FavoritesContainerComponent } from '@ruby/shared/favorites/containers/favorites.container';
import { IStation } from '@ruby/shared/models/commons/station.interface';
import { IFavoriteData } from '@ruby/shared/models/favorites/favorites.interface';
import { ICarType } from '@ruby/shared/models/rate-inquiry/cartype.interface';

@Component({
  selector: 'ng-rate-form',
  templateUrl: './rate-form.component.html',
  styleUrls: ['./rate-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RateFormComponent implements OnInit, OnChanges {
  @Input() carTypes!: Array<ICarType>;
  @Input() carOwners!: Array<INSAutoCompleteOption>;
  @Input() destinationStations!: Array<INSAutoCompleteOption>;
  @Input() displayForm!: boolean;
  @Input() originStations!: Array<INSAutoCompleteOption>;
  @Input() states!: Array<INSAutoCompleteGroup>;
  @Input() stccList!: Array<INSAutoCompleteOption>;
  @Input() isLoadingDS!: boolean;
  @Input() isLoadingOS!: boolean;
  @Input() isLoadingStcc!: boolean;
  @Input() inquiryList!: number;
  @Input() searchList!: Array<IRateSearch>;
  @Input() rateInquiryItem?: IRateInquiryItem;
  @Output() clearDynamicData = new EventEmitter();
  @Output() clearStcc = new EventEmitter();
  @Output() loadCarTypesData = new EventEmitter<string>();
  @Output() loadDestinationStationsData = new EventEmitter<IStation>();
  @Output() loadOriginStationsData = new EventEmitter<IStation>();
  @Output() loadStccData = new EventEmitter<string>();
  @Output() updateItem = new EventEmitter<IRateInquiryFavoriteItem>();
  @Output() setInquiryList = new EventEmitter<number>(true);
  @Output() setDisplayForm = new EventEmitter<boolean>();
  @Output() clearRateResultsData = new EventEmitter();
  @Output() clearRateSearchList = new EventEmitter();
  @Output() favSelected = new EventEmitter<IFavoriteData>();
  @Output() clearFavorite = new EventEmitter();
  @Output() searchRateInquiry = new EventEmitter<IRateSearch>();
  @Output() setDisplayList = new EventEmitter<boolean>();
  @Output() setCarTypes = new EventEmitter<ICarType>();
  @Output() setFavorite = new EventEmitter<Array<IRateSearch>>();
  @ViewChild('favorites') favorites!: FavoritesContainerComponent;
  searchRateInquiryForm: UntypedFormGroup = new UntypedFormGroup({
    originState: new UntypedFormControl('', [Validators.required, this.autocompleteValidator()]),
    originCity: new UntypedFormControl({ value: '' }, [Validators.required, this.autocompleteValidator()]),
    destinationState: new UntypedFormControl('', [Validators.required, this.autocompleteValidator()]),
    destinationCity: new UntypedFormControl({ value: '' }, [Validators.required, this.autocompleteValidator()]),
    carOwner: new UntypedFormControl('', Validators.required),
    carType: new UntypedFormControl({ value: '', disabled: false }, Validators.required),
    stcc: new UntypedFormControl('', [Validators.required, this.autocompleteValidator('stcc')]),
    publication: new UntypedFormControl('', Validators.pattern(NSCommonConstants.publicationNumberRegex)),
    rule11: new UntypedFormControl(false)
  });
  debounceTimeout = 200;
  selectedInquiry?: IRateSearch;
  selectedIndex!: number;
  isMediumViewport = false;
  defaultCarOwnerOption = NSRateConstants.defaultCarOwner;
  defaultCarTypeOption = NSRateConstants.defaultCarType;
  applicationType = EFavoriteApplicationType.RATE_INQUIRY;

  constructor(private observer: BreakpointObserver) {}

  get isMaxInquiry() { return this.inquiryList === NSRateConstants.maxQueries; }

  ngOnInit(): void {
    this.setup();
    this.setupDynamicFields();
    this.setupDefaultDropdowns();
    this.observer.observe(['(min-width: 992px)', '(max-width:768px)']).subscribe((result) => {
      if (result.matches) {
        this.isMediumViewport = false;
      } else {
        this.isMediumViewport = true;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rateInquiryItem && changes.rateInquiryItem.currentValue && changes.rateInquiryItem.currentValue.index >= 0 &&
      !changes.rateInquiryItem.currentValue.isEdit) {
      this.searchRateInquiryForm.patchValue(changes.rateInquiryItem.currentValue.data);
      this.searchRateInquiryForm.controls.originCity.enable();
      this.selectedInquiry = changes.rateInquiryItem.currentValue.data;
      this.selectedIndex = changes.rateInquiryItem.currentValue.index;
      this.setDisplayForm.emit(true);
    }

    if (changes.inquiryList && changes.inquiryList.currentValue >= 0) {
      this.inquiryList = changes.inquiryList.currentValue;
    }
  }

  /**
   * Is Valid Update Button
   *
   * @summary This function checks if the list should be updated or add new item
   * @returns boolean
   */
  isValidUpdateButton(): boolean {
    return !this.searchRateInquiryForm.valid || !this.searchRateInquiryForm.controls.carType.valid ||
      (this.isMaxInquiry && !this.selectedInquiry);
  }

  /**
   * Update Search List
   *
   * @summary This function update the search list and clean the form
   * @returns void
   */
  updateSearchList(): void {
    const item: IRateSearch = { ...this.searchRateInquiryForm.value } as IRateSearch;
    this.setPublication(item);
    if (this.selectedInquiry) {
      this.updateItem.emit({ inquiry: { data: item, isEdit: true, index: this.selectedIndex } });
    } else {
      if (!(this.isMaxInquiry)) {
        this.updateItem.emit({ inquiry: { data: item } });
        if (this.inquiryList >= 0) {
          this.setInquiryList.emit(this.inquiryList + 1);
        }
      }
    }
    this.clearSearchForm();
  }

  /**
   * Search
   *
   * @summary Search Rate Inquiry
   * @returns void
   */
  search(): void {
    const form: IRateSearch = { ...this.searchRateInquiryForm.value } as IRateSearch;
    this.setPublication(form);
    this.searchRateInquiry.emit(form);
    this.setDisplayForm.emit(false);
  }

  /**
   * Clear Search Form
   *
   * @summary Clear the search form
   * @returns void
   */
  clearSearchForm(): void {
    this.searchRateInquiryForm.reset();
    this.setupDefaultDropdowns();
    this.resetInitalFormValues();
    this.clearDynamicData.emit();
    this.selectedInquiry = undefined;
  }

  /**
   * Reset Rate Form Initial Values
   *
   * @summary Reset initial form values for unrequired form controls: Publication & Rule11
   * @returns void
   */
  resetInitalFormValues(): void {
    this.searchRateInquiryForm.get('publication')?.reset('');
    this.searchRateInquiryForm.get('rule11')?.reset(false);
  }

  /**
   * Set Rate Form: Car Owner's & Car Type's Default Values
   *
   * @summary Set default rate form option for form controls: Car Owner & Car Type
   * @returns void
   */
  setupDefaultDropdowns(): void {
    this.searchRateInquiryForm.get('carOwner')?.patchValue(this.defaultCarOwnerOption);
    this.searchRateInquiryForm.get('carType')?.patchValue(this.defaultCarTypeOption);
  }

  /**
   * Car Owner Comparator
   *
   * @summary Method used to compare 2 INSAutoCompleteOption objects to be displayed in mat-select
   * @param o1: INSAutoCompleteOption
   * @param o2: INSAutoCompleteOption
   * @returns boolean
   */
  carOwnerComparator(o1?: INSAutoCompleteOption, o2?: INSAutoCompleteOption): boolean {
    const result: boolean | undefined = o1 && o2 && o1.key === o2.key;
    return result ? result : false;
  }

  /**
   * Car Type Comparator
   *
   * @summary Method used to compare 2 ICarType objects to be displayed in mat-select
   * @param o1: ICarType
   * @param o2: ICarType
   * @returns boolean
   */
  carTypeComparator(o1?: ICarType, o2?: ICarType): boolean {
    const result: boolean | undefined = o1 && o1.key && o2 && o2.key && o1.key.carTypeCode === o2.key.carTypeCode;
    return result ? result : false;
  }

  /**
   * autocomplete Selected
   *
   * @summary Method called when autocomplete is selected
   * @param formCtrlName: string
   * @param previousCtrlName: string
   * @returns void
   */
  autocompleteSelected(formCtrlName: string, previousCtrlName: string): void {
    if (typeof this.searchRateInquiryForm.get(previousCtrlName)?.value !== 'string') {
      this.searchRateInquiryForm.get(formCtrlName)?.enable();
    }
    if (formCtrlName === 'carType') {
      this.searchRateInquiryForm.get('carType')?.patchValue(this.defaultCarTypeOption);
      this.loadCarTypes(this.searchRateInquiryForm.controls.stcc.value);
    }
  }

  /**
   * Autocomplete Validator
   *
   * @summary Autocomplete Validator
   * @returns ValidatorFn
   */
  autocompleteValidator(controlName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let result = {};
      if (typeof control.value === 'string' && control.value !== '') {
        result = { invalidSelection: { value: control.value } };
      }

      if (controlName === 'stcc' && typeof control.value === 'string' && control.value.length > 7) {
        return { maxlength: { requiredLength: 7 } };
      }

      return result;
    };
  }

  /**
   * Toggle Rate Inquiry Form
   *
   * @summary Toggle rate inquiry form
   * @returns void
   */
  toggleRateInquiryForm(): void {
    this.setDisplayForm.emit(!this.displayForm);
  }

  /**
   * New Inquiry
   *
   * @summary Clear All Rate Search & Results Data
   * @returns void
   */
  newInquiry(): void {
    this.clearSearchForm();
    this.clearRateResultsData.emit();
    this.favorites.clearForm();
    this.clearFavorite.emit();
  }

  /**
   * Favorite Selected
   *
   * @summary This function will emil the selected favorite
   * @param favorite: IFavoriteData
   * @returns void
   */
  favoritesSelected(favorite: IFavoriteData): void {
    this.favSelected.emit(favorite);
    this.setDisplayList.emit(true);
  }

  /**
   * Favorite Saved
   *
   * @summary Update inquiry list when favorite is saved/updated
   * @param favorite: IFavoriteData
   * @returns void
   */
  favoriteSaved(favorite: IFavoriteData): void {
    const item: IRateSearch = { ...this.searchRateInquiryForm.value } as IRateSearch;
    this.setPublication(item);
    if (this.selectedInquiry) {
      this.updateItem.emit({ inquiry: { data: item, isEdit: true, index: this.selectedIndex }, favorite });
      this.clearSearchForm();
    } else if (this.searchRateInquiryForm.valid) {
      if (!(this.isMaxInquiry)) {
        this.updateItem.emit({ inquiry: { data: item }, favorite });
        if (this.inquiryList >= 0) {
          this.setInquiryList.emit(this.inquiryList + 1);
        }
      }
      this.clearSearchForm();
    }
  }

  private loadCarTypes(stcc: INSAutoCompleteOption): void {
    if (stcc) {
      this.loadCarTypesData.emit(stcc.key);
    }
  }

  //@TODO Fix this
  /* istanbul ignore next */
  private loadStcc(stcc: string | INSAutoCompleteOption) {
    if (typeof stcc === 'string') {
      if (stcc.length > 2) {
        this.loadStccData.emit(stcc.toUpperCase());
        this.searchRateInquiryForm.controls.carType.disable();
      } else {
        this.clearStcc.emit();
      }
    }
  }

  private setupDynamicFields(): void {
    this.searchRateInquiryForm.controls.originCity.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged())
      .subscribe((stationName: string) => {
        if (stationName) {
          this.loadOriginStationsData.emit({
            stateAbbreviation: this.searchRateInquiryForm.controls.originState.value.key,
            station: stationName
          });
        }
      });

    this.searchRateInquiryForm.controls.destinationCity.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged())
      .subscribe((stationName: string) => {
        if (stationName) {
          this.loadDestinationStationsData.emit({
            stateAbbreviation: this.searchRateInquiryForm.controls.destinationState.value.key,
            station: stationName
          });
        }
      });
  }

  private setup(): void {
    this.searchRateInquiryForm.controls.originState.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged()
    ).subscribe((state: string | INSAutoCompleteOption) => {
      if (!state || typeof state === 'string') {
        this.searchRateInquiryForm.controls.originCity.reset();
        this.searchRateInquiryForm.controls.originCity.disable();
      }
    });

    this.searchRateInquiryForm.controls.destinationState.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged()
    ).subscribe((state: string | INSAutoCompleteOption) => {
      if (!state || typeof state === 'string') {
        this.searchRateInquiryForm.controls.destinationCity.reset();
        this.searchRateInquiryForm.controls.destinationCity.disable();
      }
    });

    this.searchRateInquiryForm.controls.stcc.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged()
    ).subscribe((stcc: string | INSAutoCompleteOption) => {
      this.loadStcc(stcc);
    });

    this.searchRateInquiryForm.statusChanges.pipe(
      debounceTime(this.debounceTimeout)
    ).subscribe((status: string) => {
      if (status === 'VALID') {
        if (this.searchList.length <= NSRateConstants.maxQueries) {
          const item: IRateSearch = { ...this.searchRateInquiryForm.value } as IRateSearch;
          this.setPublication(item);
          if (this.selectedInquiry) {
            const rateSearchList: Array<IRateSearch> = [...this.searchList];
            rateSearchList.splice(this.selectedIndex, 1, item);
            this.setFavorite.emit(rateSearchList);
          } else if (this.searchList.length < NSRateConstants.maxQueries) {
            const list: Array<IRateSearch> = [...this.searchList, item];
            this.setFavorite.emit(list);
          }
        }
      }
    });
  }

  private setPublication(item: IRateSearch): void {
    const publicationValue = this.searchRateInquiryForm.controls.publication.value;
    if (publicationValue && publicationValue.match(NSCommonConstants.publicationNumberRegex)) {
      item.authorityNumber = publicationValue.replace(/\D/g, '');
      item.publishAuthority = publicationValue.replace(/[^a-z]/gi, '');
    }
  }
}
